import { useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useRequestPhoneUpdateOtp } from "@/features/auth/services";
import { phoneOtpSchema } from "@/features/auth/lib/phone-otp-schema";
import {
  getPhoneVerificationState,
  updatePhoneVerificationState,
} from "@/shared/utils/phoneVerification";
import Button from "../../../shared/components/Button";
import Input from "../../../shared/components/Input";
import LinearLayout from "../../../shared/components/LinearLayout";

const getRedirect = (value?: string | null) =>
  value && value.length > 0 ? value : null;

export default function PhoneVerifyForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locationState =
    (location.state as { phoneNumber?: string; redirectTo?: string } | null) ??
    null;
  const queryPhone = searchParams.get("phone");
  const queryRedirect = searchParams.get("redirectTo");
  const storedPhoneVerification = getPhoneVerificationState();
  const [phoneNumber, setPhoneNumber] = useState(
    locationState?.phoneNumber ??
      queryPhone ??
      storedPhoneVerification.phoneNumber ??
      ""
  );
  const redirectTo =
    getRedirect(locationState?.redirectTo) ??
    getRedirect(queryRedirect) ??
    "/dashboard";
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: requestPhoneOtp, isPending } =
    useRequestPhoneUpdateOtp();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const result = phoneOtpSchema.safeParse(phoneNumber);

    if (!result.success) {
      setError(result.error.issues[0]?.message ?? "Invalid phone number");
      return;
    }

    const normalizedPhone = result.data;

    try {
      await requestPhoneOtp({ phoneNumber: normalizedPhone });
      updatePhoneVerificationState({
        phoneNumber: normalizedPhone,
        verified: false,
      });
      toast.success("Verification code sent to your phone number.");

      const params = new URLSearchParams();
      params.set("phone", normalizedPhone);
      if (redirectTo) {
        params.set("redirectTo", redirectTo);
      }

      navigate(`/verify-phone/code?${params.toString()}`, {
        state: { phoneNumber: normalizedPhone, redirectTo },
        replace: true,
      });
    } catch (submissionError) {
      const message =
        submissionError instanceof Error
          ? submissionError.message
          : "Unable to send verification code. Please try again.";
      toast.error(message);
      setError(message);
    }
  };

  return (
    <div className="w-full">
      <LinearLayout orientation="vertical">
        <form
          onSubmit={handleSubmit}
          className="text-center align-middle mx-auto space-y-5">
          <h1 className="text-white text-[20px] leading-[24px] font-medium ">
            Verify Your Phone Number
          </h1>
          <p className="text-white text-sm font-sm opacity-50">
            We will send a verification code to your phone number
          </p>
          <div>
            <Input
              type="tel"
              placeholder="Enter phone number"
              fluid
              value={phoneNumber}
              onChange={(event) => {
                setPhoneNumber(event.target.value);
                if (error) {
                  setError(null);
                }
              }}
            />
            {error && (
              <p className="text-danger text-sm text-left mt-1">{error}</p>
            )}
          </div>
          <Button type="submit" variant="primary" fluid loading={isPending}>
            {isPending ? "Sending..." : "Send Code"}
          </Button>
          <p className="text-white/70 text-xs">
            You will be redirected automatically after verification.
          </p>
        </form>
      </LinearLayout>
    </div>
  );
}
